import { ref, computed } from 'vue'
import useWorkspaces from '@/store/workspaces'
import { useBuilderApiClient } from '@/api/builder/useBuilderApiClient'
import { ENDPOINTS } from '@/consts/api'
import { postWorkspaceSecret } from '@/api/dashboard/functions/settings'
import { promiseTimeout } from '@vueuse/core'
import { ENDPOINTS as RUN_ENDPOINTS } from '@/modules/workflow-run/api'
import { omit } from 'ramda'
import type { WorkspaceType } from '@/types/workspaces'

const { workspaces } = useWorkspaces()

// STATE
const currentWorkspaceId = ref<Nullable<string>>(null)
const currentWorkspace = computed(() => workspaces.value.find(w => w.id === currentWorkspaceId.value))
const currentWorkspaceType = computed<WorkspaceType>(() => (currentWorkspace.value?.workspaceType.toLowerCase() || 'sandbox') as WorkspaceType)

// MUTATIONS
const setCurrentWorkspace = async (workspaceId: string | null) => {
    if (workspaceId === currentWorkspaceId.value) return

    currentWorkspaceId.value = workspaceId
    if (workspaceId) {
        localStorage.setItem('tl-dashboard-workspace-id', workspaceId)
    } else {
        localStorage.removeItem('tl-dashboard-workspace-id')
    }
    await promiseTimeout(10)

    return true
}

const generateNewSecret = async (workspaceId: string) => {
    const response = await postWorkspaceSecret({
        workspaceId,
        workspaceType: currentWorkspaceType.value,
    }).call()

    return response?.data.newSecret
}

const generateLink = async (
    workspaceId: Nullable<string>,
    payload: Record<string, unknown>,
    headers?: Record<string, string>,
    useV3Endpoint?: boolean,
    locale?: string,
    expiresAt?: string,
): Promise<Record<'link', string>> => {
    if (useV3Endpoint) {

        const { call } = useBuilderApiClient<Record<'link', string>>({
            method: 'POST',
            url: RUN_ENDPOINTS.WORKFLOWS,
            urlParams: {
                workspaceId: workspaceId as string,
            },
            data: {
                ...omit(['parametersId'], payload),
                ...locale ? { locale } : {},
                ...expiresAt ? { expiresAt } : {},
            },
        })

        const response = await call()

        return { link: response?.data.link || '' }
    }

    const { call } = useBuilderApiClient<Record<'link', string>>({
        method: 'POST',
        url: ENDPOINTS.LINK,
        urlParams: {
            workspaceId: workspaceId as string,
        },
        data: {
            ...payload,
        },
        ...headers ? { headers } : {},
    })

    const response = await call()

    return { link: response?.data.link || '' }
}

localStorage.getItem('e2e') === 'true' &&
Object.assign(window, { currentWorkspace })

// FACTORY
export default function useWorkspace() {
    return {
        currentWorkspaceId,
        currentWorkspace,
        currentWorkspaceType,
        setCurrentWorkspace,
        generateNewSecret,
        generateLink,
    }
}
